import { Link } from "react-router-dom";
import { TrendingDown, DollarSign, Clock, Wallet } from "lucide-react";
import { EDGE_NAVY } from "../../lib/edge";
import MortgageDisclosure from "../../components/MortgageDisclosure";
import Seo from "../../components/Seo";

const REASONS = [
  { icon: <TrendingDown className="w-5 h-5" />, title: "Lower your rate", text: "Refinance to a lower fixed rate when market conditions allow." },
  { icon: <Clock className="w-5 h-5" />, title: "Shorter term", text: "Move from 30-yr to 15-yr to build equity and save on interest." },
  { icon: <DollarSign className="w-5 h-5" />, title: "Cash-out", text: "Tap home equity for renovations, debt consolidation, or investment." },
  { icon: <Wallet className="w-5 h-5" />, title: "Drop PMI", text: "Refinance once you cross 20% equity to remove mortgage insurance." },
];

export default function Refinance() {
  return (
    <div className="bg-stone-50 min-h-screen" data-testid="refinance-page">
      <Seo
        title="Refinance Your Houston Home"
        description="Refinance your Houston mortgage with Edge Home Finance — rate-and-term refinance, cash-out refinance, and HELOC options."
        path="/refinance"
      />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-12">
        <div className="text-[10px] uppercase tracking-[0.3em] mb-3" style={{ color: EDGE_NAVY }}>
          Edge Home Finance · Refinance
        </div>
        <h1 className="font-display text-4xl lg:text-6xl font-light tracking-tighter text-[#1A1A1A] max-w-3xl">
          Make your mortgage <em className="not-italic" style={{ color: EDGE_NAVY }}>work</em> for you.
        </h1>
        <p className="text-[#555] mt-5 max-w-2xl text-lg">
          A refinance can lower your rate, shorten your term, drop PMI, or unlock cash from your equity.
          Pierre will run the numbers honestly — and only recommend it when it actually makes sense.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {REASONS.map((r) => (
            <div key={r.title} className="bg-white border border-gray-200 rounded-xl p-7">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center mb-4"
                style={{ background: `${EDGE_NAVY}1A`, color: EDGE_NAVY }}
              >
                {r.icon}
              </div>
              <div className="font-display text-lg font-semibold text-[#1A1A1A]">{r.title}</div>
              <p className="text-sm text-[#555] mt-1.5 leading-relaxed">{r.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="cash-out" className="max-w-7xl mx-auto px-6 lg:px-10 pb-16 scroll-mt-28">
        <div className="bg-white border border-gray-200 rounded-2xl p-10 lg:p-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <h2 className="font-display text-3xl lg:text-4xl font-light tracking-tighter">
              Cash-out refinance
            </h2>
            <p className="text-[#444] leading-relaxed mt-4">
              In Texas, cash-out refinances are governed by{" "}
              <strong>Section 50(a)(6) of the Texas Constitution</strong> — limiting cash-out to 80% LTV with specific
              consumer protections. Edge Home Finance ensures every Texas cash-out refi follows these rules to the
              letter.
            </p>
            <ul className="mt-5 space-y-2 text-sm text-[#1A1A1A]">
              <li>• Maximum 80% combined loan-to-value</li>
              <li>• 12-day waiting period after application</li>
              <li>• 3-day right of rescission after closing</li>
              <li>• Once a property has been refinanced under 50(a)(6), all future refis are subject to the same rules</li>
            </ul>
          </div>
          <div className="lg:col-span-5 bg-stone-50 rounded-xl p-7">
            <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-2">Ready to explore?</div>
            <h3 className="font-display text-2xl font-semibold">Run the numbers with Pierre</h3>
            <p className="text-sm text-[#555] mt-3 leading-relaxed">
              We'll compare your current loan to refi options side-by-side — and tell you flat-out if it doesn't
              make sense to refinance right now.
            </p>
            <Link
              to="/pre-approval"
              state={{ program: "Refinance" }}
              className="block text-center mt-5 bg-[#E63946] hover:bg-[#c9313d] text-white px-6 py-3 rounded-md font-semibold text-sm"
              data-testid="refi-cta"
            >
              Start Refinance Inquiry
            </Link>
          </div>
        </div>
      </section>

      <MortgageDisclosure />
    </div>
  );
}
